import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp, Plus, X, ThumbsUp, Trash2, CalendarPlus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getSession } from '../../lib/supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export default function Wishlist({ tripId, userRole, currentUserId, onAddToItinerary }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newPlace, setNewPlace] = useState('');
  const [newNotes, setNewNotes] = useState('');

  const canEdit = userRole === 'owner' || userRole === 'editor';

  // Get auth headers by reading token from localStorage (fast) or getSession() (fallback)
  const getAuthHeaders = useCallback(async () => {
    const headers = { 'Content-Type': 'application/json' };
    try {
      const storageKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
      if (storageKey) {
        const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
        if (stored?.access_token) {
          headers['Authorization'] = `Bearer ${stored.access_token}`;
          return headers;
        }
      }
      const session = await getSession();
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
    } catch { /* proceed without auth */ }
    return headers;
  }, []);

  // Fetch wishlist items
  const fetchItems = useCallback(async () => {
    if (!tripId) return;
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${BACKEND_URL}/api/trips/${tripId}/wishlist`, {
        headers,
      });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.items || [];
        setItems(list);
        // Auto-expand if there are items
        if (list.length > 0) setExpanded(true);
      }
    } catch (err) {
      console.error('Failed to fetch wishlist:', err);
    } finally {
      setLoading(false);
    }
  }, [tripId, getAuthHeaders]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Add item
  const handleAdd = useCallback(async (e) => {
    e.preventDefault();
    if (!newPlace.trim() || submitting) return;

    setSubmitting(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${BACKEND_URL}/api/trips/${tripId}/wishlist`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          placeName: newPlace.trim(),
          notes: newNotes.trim() || undefined,
        }),
      });
      if (res.ok) {
        const item = await res.json();
        setItems(prev => [...prev, item]);
        setNewPlace('');
        setNewNotes('');
        setShowForm(false);
      }
    } catch (err) {
      console.error('Failed to add wishlist item:', err);
    } finally {
      setSubmitting(false);
    }
  }, [tripId, newPlace, newNotes, submitting, getAuthHeaders]);

  // Toggle vote
  const handleVote = useCallback(async (itemId) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${BACKEND_URL}/api/trips/${tripId}/wishlist/${itemId}/vote`, {
        method: 'POST',
        headers,
      });
      if (res.ok) {
        const updated = await res.json();
        setItems(prev => prev.map(item =>
          item.id === itemId ? { ...item, ...updated } : item
        ));
      }
    } catch (err) {
      console.error('Failed to vote:', err);
    }
  }, [tripId, getAuthHeaders]);

  // Remove item
  const handleRemove = useCallback(async (itemId) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${BACKEND_URL}/api/trips/${tripId}/wishlist/${itemId}`, {
        method: 'DELETE',
        headers,
      });
      if (res.ok) {
        setItems(prev => prev.filter(item => item.id !== itemId));
      }
    } catch (err) {
      console.error('Failed to remove wishlist item:', err);
    }
  }, [tripId, getAuthHeaders]);

  // Update items from realtime events
  const handleRealtimeUpdate = useCallback((eventType, payload) => {
    if (eventType === 'INSERT') {
      setItems(prev => {
        if (prev.some(i => i.id === payload.id)) return prev;
        return [...prev, payload];
      });
    } else if (eventType === 'UPDATE') {
      setItems(prev => prev.map(i => i.id === payload.id ? { ...i, ...payload } : i));
    } else if (eventType === 'DELETE') {
      setItems(prev => prev.filter(i => i.id !== payload.id));
    }
  }, []);

  // Expose for parent to wire realtime
  Wishlist.handleRealtimeUpdate = handleRealtimeUpdate;

  if (loading) {
    return (
      <div className="mx-4 mb-3 bg-white border border-gray-200 rounded-xl p-4 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
      </div>
    );
  }

  return (
    <div className="mx-4 mb-3 bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-semibold text-gray-800">
          Wishlist {items.length > 0 && `(${items.length} ${items.length === 1 ? 'idea' : 'ideas'})`}
        </span>
        <div className="flex items-center gap-2">
          {canEdit && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(true);
                setShowForm(true);
              }}
              className="p-1 rounded-md hover:bg-teal-50 text-teal-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
            </span>
          )}
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {items.length === 0 && !showForm && (
            <p className="text-sm text-gray-400 pt-3">
              No ideas yet. {canEdit ? 'Add places you want to visit!' : ''}
            </p>
          )}

          {/* Items */}
          {items.length > 0 && (
            <div className="space-y-2 pt-3">
              {items.map((item) => {
                const voted = item.voted_by_current_user ||
                  (item.votes && Array.isArray(item.votes) && item.votes.includes(currentUserId));
                const voteCount = item.vote_count ?? (item.votes ? item.votes.length : 0);

                return (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.place_name || item.placeName}
                      </p>
                      {item.notes && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">{item.notes}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">
                        Added by {item.added_by_name || item.addedByName || 'someone'}
                      </p>
                    </div>

                    {/* Vote button */}
                    <button
                      onClick={() => handleVote(item.id)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors
                        ${voted
                          ? 'bg-teal-100 text-teal-700 border border-teal-200'
                          : 'bg-white text-gray-500 border border-gray-200 hover:border-teal-200 hover:text-teal-600'
                        }`}
                    >
                      <ThumbsUp className="w-3.5 h-3.5" />
                      {voteCount > 0 && <span>{voteCount}</span>}
                    </button>

                    {/* Add to itinerary */}
                    {onAddToItinerary && canEdit && (
                      <button
                        onClick={() => onAddToItinerary(item.place_name || item.placeName)}
                        className="p-1 rounded hover:bg-teal-50 text-gray-300 hover:text-teal-600 opacity-0 group-hover:opacity-100 transition-all"
                        title="Add to itinerary"
                      >
                        <CalendarPlus className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {/* Remove button */}
                    {canEdit && (
                      <button
                        onClick={() => handleRemove(item.id)}
                        className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}

              <p className="text-xs text-gray-400 pt-1">
                Hover to add items to the current day
              </p>
            </div>
          )}

          {/* Add form */}
          {showForm && canEdit && (
            <form onSubmit={handleAdd} className="pt-3 space-y-2">
              <input
                type="text"
                value={newPlace}
                onChange={(e) => setNewPlace(e.target.value)}
                placeholder="Place name (e.g., Tsukiji Market)"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                autoFocus
              />
              <input
                type="text"
                value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)}
                placeholder="Notes (optional)"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={!newPlace.trim() || submitting}
                  className="px-4 py-1.5 text-sm font-medium bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Adding...' : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setNewPlace(''); setNewNotes(''); }}
                  className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
